import { env } from "@/env";
import { CACHE_TAG_REGISTRY_BASE } from "@mw/shared/cacheTagRegistry";
import { simpleStringHash } from "@mw/shared/hash";

export type { CacheTagMeta, CacheStore, KnownCacheTag } from "@mw/shared/cacheTagRegistry";
export { CACHE_PERSISTENT_TAG, KNOWN_CACHE_TAGS } from "@mw/shared/cacheTagRegistry";

import type { KnownCacheTag } from "@mw/shared/cacheTagRegistry";

// Per-tag revalidate TTL (seconds). Optional — tags without an entry fall
// back to `defaultRevalidate` from the shared registry.
const REVALIDATE_OVERRIDES: Partial<Record<KnownCacheTag, number>> = {
  "xi-assets-cache": env.NEXT_PUBLIC_XI_ASSETS_CACHE_PERIOD,
  "xi-shared-sounds": env.NEXT_PUBLIC_XI_ASSETS_CACHE_PERIOD,
  "xi-shared-generations": env.NEXT_PUBLIC_XI_ASSETS_CACHE_PERIOD,
  "xi-music": env.NEXT_PUBLIC_XI_ASSETS_CACHE_PERIOD,
  "xi-iconic-voices": env.NEXT_PUBLIC_XI_ASSETS_CACHE_PERIOD,
  "ashby-jobs": env.NEXT_PUBLIC_ASHBY_CACHE_PERIOD,
  "music-song": env.NEXT_PUBLIC_CACHE_PERIOD,
  "soundboard-sounds": env.NEXT_PUBLIC_CACHE_PERIOD,
  "find-slug": env.NEXT_PUBLIC_CACHE_PERIOD,
  "posthog-feature-flags": env.NEXT_PUBLIC_CACHE_PERIOD,
  "markdown-cache": env.NEXT_PUBLIC_PAYLOAD_CACHE_PERIOD,
};

export const CACHE_TAG_REGISTRY = CACHE_TAG_REGISTRY_BASE.map(e => ({
  ...e,
  revalidate: REVALIDATE_OVERRIDES[e.tag as KnownCacheTag] ?? e.defaultRevalidate,
}));

export const CACHE_TAG_REVALIDATE = Object.fromEntries(CACHE_TAG_REGISTRY.map(e => [e.tag, e.revalidate])) as Record<
  KnownCacheTag,
  number
>;

const DEFAULT_JITTER_FRACTION = 0.2;

/**
 * Stable revalidate TTL (seconds) in `[base * (1 - jitter), base * (1 + jitter)]`.
 * Spreads background refreshes to avoid thundering herds against rate-limited APIs.
 */
export function revalidateWithJitter(
  baseSeconds: number,
  seedParts: string[],
  jitterFraction = DEFAULT_JITTER_FRACTION,
) {
  if (baseSeconds <= 0 || jitterFraction <= 0) {
    return baseSeconds;
  }

  const normalized = seedParts.join("\0");
  const unit = ((simpleStringHash(normalized) >>> 0) % 10_000) / 10_000;
  const multiplier = 1 - jitterFraction + unit * 2 * jitterFraction;
  return Math.max(1, Math.round(baseSeconds * multiplier));
}
